import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useCreateFlowInstanceMutation } from '@/services/endpoints/workspace-connectors';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type UseSmsSenderDeleteArgs = {
  deleteFlowId?: string;
  connectorInstanceId?: string;
};

export const useSmsSenderDelete = ({ deleteFlowId, connectorInstanceId }: UseSmsSenderDeleteArgs) => {
  const { t } = useTranslation('campaign');
  const workspace = useWorkspace();
  const { audienceId } = useFirstAudienceId();
  const appDispatch = useAppDispatch();
  const [createFlowInstance, { isLoading: isDeleting }] = useCreateFlowInstanceMutation();

  const deleteSender = useCallback(
    async (senderId: string) => {
      if (!deleteFlowId) {
        appDispatch(
          showToast({
            type: ToastType.ERROR,
            title: String(t('smsWorkspace.senders.missingDeleteFlow')),
          })
        );
        return false;
      }

      if (!connectorInstanceId) {
        appDispatch(
          showToast({
            type: ToastType.ERROR,
            title: String(t('smsWorkspace.senders.missingConnectorInstance')),
          })
        );
        return false;
      }

      if (!audienceId) {
        appDispatch(
          showToast({
            type: ToastType.ERROR,
            title: String(t('missingAudience.toast')),
          })
        );
        return false;
      }

      try {
        const payload = buildFlowInstancePayload({
          workspaceId: workspace.id,
          connectorFlowId: deleteFlowId,
          connectorInstanceId,
          audienceId,
          flowParams: { alias_id: senderId },
          flowMappings: {},
        });

        await createFlowInstance(payload).unwrap();

        appDispatch(
          showToast({
            type: ToastType.SUCCESS,
            title: String(t('smsWorkspace.senders.deleteSuccess')),
          })
        );

        return true;
      } catch (error) {
        const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('smsWorkspace.senders.deleteFailed'));
        appDispatch(
          showToast({
            type: ToastType.ERROR,
            title: errorMessage,
          })
        );
        return false;
      }
    },
    [appDispatch, audienceId, connectorInstanceId, createFlowInstance, deleteFlowId, t, workspace.id]
  );

  return {
    deleteSender,
    isDeleting,
  };
};
